import { isApiEnabled } from './config';
import { waitForDeviceSession } from './device-session';
import { putProfile } from './profile';

import type { ProfileBody, ProfileSyncDeps, ProfileSyncOutcome } from './types';

/**
 * profile-sync — 로컬이 정본이고 서버는 뒤따라간다.
 *
 * 화면은 저장을 기다리지 않는다(오프라인에서도 온보딩이 끝나야 한다). 그래서 보낼 것을
 * 이 키에 쌓아 두고, 부팅·온라인 복귀 때 비운다.
 *
 * **재시도 판정이 이 파일의 존재 이유다.** 전부 재시도하면 페이로드가 틀린 요청이
 * 영영 안 지워져 큐가 poison pill 이 되고, 전부 포기하면 세션이 아직 안 선
 * 첫 요청(`403`)에서 동기가 통째로 사라진다 — `useDeviceIdentity` 는 `void` 라
 * 아무도 세션을 기다릴 수 없어서 그 403 은 고장이 아니라 정상 경로다.
 */
export const PROFILE_PENDING_KEY = 'muzly.profile.pending.v1';

/** 다시 보낼 값 — 세션 대기(403)·혼잡(429)·서버 잘못(5xx)·상태 모름(네트워크). */
/** 큐 flush 가 기기 세션을 기다리는 상한. 넘으면 다음 기회로 미룬다. */
const SESSION_WAIT_MS = 2_000;

const isRetryable = (status: number | undefined): boolean =>
  status === undefined || status >= 500 || status === 403 || status === 429;

export const loadPendingProfile = (
  storage: Pick<Storage, 'getItem'>,
): ProfileBody | null => {
  const raw = storage.getItem(PROFILE_PENDING_KEY);
  if (!raw) {
    return null;
  }
  try {
    const pending = JSON.parse(raw) as ProfileBody;
    return typeof pending === 'object' && pending !== null ? pending : null;
  } catch {
    return null;
  }
};

const defaultDeps = (): ProfileSyncDeps => ({
  storage: localStorage,
  put: putProfile,
  enabled: isApiEnabled,
  awaitSession: waitForDeviceSession,
  warn: (message, error) => console.warn(`[profile-sync] ${message}`, error),
});

/**
 * 큐가 채워졌다는 신호를 받을 곳.
 *
 * `useProfileSync` 는 보낼 것이 없으면 한 번 확인하고 멈춘다(그 편이 죽은 백엔드를
 * 두드리지 않는다). 그런데 초기 설정·프로필 수정은 **부팅 뒤에** 큐를 채우므로, 멈춘
 * flush 를 깨울 길이 없으면 그 답은 다음 앱 실행까지 서버에 안 간다 — 설문만 하고 앱을
 * 다시 안 여는 사람은 영영 안 간다. 그래서 쌓는 쪽이 알린다.
 */
type QueueListener = () => void;
const listeners = new Set<QueueListener>();

/** 구독한다. 반환값을 부르면 끊는다. */
export const subscribeProfileQueue = (listen: QueueListener): (() => void) => {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
};

/** 보낼 것을 쌓는다 — 같은 필드는 나중 값이 이긴다. */
export const queueProfileSync = (
  patch: ProfileBody,
  deps: ProfileSyncDeps = defaultDeps(),
): void => {
  const merged = { ...(loadPendingProfile(deps.storage) ?? {}), ...patch };
  deps.storage.setItem(PROFILE_PENDING_KEY, JSON.stringify(merged));
  listeners.forEach((listen) => listen());
};

/**
 * 방금 보낸 것만 지운다. 요청이 나가 있는 동안 쌓인 값(진단 시작이 컨디션을 넣는 경우)을
 * 통째로 지우면 그 답이 서버에 영영 안 간다.
 */
const clearSent = (
  storage: ProfileSyncDeps['storage'],
  sent: ProfileBody,
): void => {
  const current = loadPendingProfile(storage);
  if (!current) {
    return;
  }
  const rest = Object.entries(current).filter(
    ([key, value]) =>
      !(key in sent) || value !== sent[key as keyof ProfileBody],
  );
  if (rest.length === 0) {
    storage.removeItem(PROFILE_PENDING_KEY);
    return;
  }
  storage.setItem(
    PROFILE_PENDING_KEY,
    JSON.stringify(Object.fromEntries(rest)),
  );
};

/** 쌓아 둔 것을 한 번 보낸다. 실패는 화면에 띄우지 않는다. */
export const syncProfile = async (
  deps: ProfileSyncDeps = defaultDeps(),
): Promise<ProfileSyncOutcome> => {
  const pending = loadPendingProfile(deps.storage);
  if (!pending) {
    return 'idle';
  }
  // 백엔드가 없으면 세션은 영영 안 선다 — 기다리면 편집 한 번마다 상한을 통째로 버린다.
  // 쌓아 둔 값은 그대로 남아, 주소가 생긴 뒤 첫 flush 에 올라간다.
  if (!deps.enabled()) {
    return 'retry';
  }
  // 세션 없이 보내면 서명이 없어 403 이고, 그 403 은 재시도로 안 풀린다. 다음 기회에 보낸다.
  if (!(await deps.awaitSession(SESSION_WAIT_MS))) {
    return 'retry';
  }
  try {
    await deps.put(pending);
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (isRetryable(status)) {
      return 'retry';
    }
    clearSent(deps.storage, pending);
    deps.warn(
      `dropped a payload the server refused (${String(status)})`,
      error,
    );
    return 'dropped';
  }
  clearSent(deps.storage, pending);
  return 'sent';
};
